// Stuck-media detection and the bounded rebuild schedule for the softphone's
// live line. Pure: use-softphone owns the timers, the SIP calls and the
// telemetry; this module only decides.
//
// The fault: ICE reaches connected but the DTLS handshake never finishes, so
// the peer connection sits in "connecting" (or goes "failed") with ICE still
// reporting connected. No SRTP flows, FreeSWITCH gets no audio from the agent,
// and a pool line in that state takes prospects into dead air. The ICE-driven
// recovery never fires because ICE itself is fine, and an ICE restart keeps
// the same DTLS transport — only a fresh peer connection redoes the handshake.

// A healthy handshake completes within a second of ICE connecting, relay
// included; eight seconds past that is well clear of any slow-but-working
// setup.
export const MEDIA_STUCK_CONNECTING_MS = 8_000;

// Rebuilds per episode. The episode resets once a peer connection reaches
// "connected"; after the last one the agent is told, and nothing loops.
export const MEDIA_REBUILD_MAX_ATTEMPTS = 3;
const MEDIA_REBUILD_DELAYS_MS = [0, 5_000, 15_000];

// After the un-REGISTER, how long to wait for the switch to hang up the parked
// line itself. The pool reaps a free or connecting member's leg on the
// sofia::unregister event (milliseconds); it never reaps a line carrying a
// conversation, so a bridged line waits only long enough to prove it was not
// parked after all.
export const MEDIA_REBUILD_REAP_WAIT_MS = 5_000;
export const MEDIA_REBUILD_BRIDGED_REAP_WAIT_MS = 1_500;

// How long a rebuild waits for its replacement line to connect before it is
// written off. A parked seat's line comes back as soon as the phone
// re-registers (~9 s typical); a bridged one only after the agent finishes
// wrap-up, which is time-boxed at a minute and can be extended.
const MEDIA_REBUILD_CONFIRM_MS = 30_000;
const MEDIA_REBUILD_BRIDGED_CONFIRM_MS = 180_000;

export type MediaPathVerdict =
  // Connected, torn down, or an ICE problem the ICE-restart path owns.
  | { kind: 'clear' }
  | { kind: 'watching'; sinceMs: number; fireInMs: number }
  | { kind: 'stuck'; stuckMs: number };

function iceUp(iceConnectionState: string): boolean {
  return iceConnectionState === 'connected' || iceConnectionState === 'completed';
}

// `stuckSinceMs` is when this peer connection was first seen with ICE up and
// the connection not; the caller keeps it across calls and resets it on
// "clear". `dtlsState` is the RTCDtlsTransport's own state where the browser
// exposes it (null otherwise): a connected transport overrules the aggregate.
export function assessMediaPath(input: {
  connectionState: string;
  iceConnectionState: string;
  dtlsState: string | null;
  stuckSinceMs: number | null;
  nowMs: number;
}): MediaPathVerdict {
  const { connectionState, iceConnectionState, nowMs } = input;
  if (!iceUp(iceConnectionState) || input.dtlsState === 'connected') return { kind: 'clear' };
  const sinceMs = input.stuckSinceMs ?? nowMs;
  if (connectionState === 'failed') return { kind: 'stuck', stuckMs: nowMs - sinceMs };
  if (connectionState !== 'connecting' && connectionState !== 'new') return { kind: 'clear' };
  const elapsedMs = nowMs - sinceMs;
  if (elapsedMs >= MEDIA_STUCK_CONNECTING_MS) return { kind: 'stuck', stuckMs: elapsedMs };
  return { kind: 'watching', sinceMs, fireInMs: MEDIA_STUCK_CONNECTING_MS - elapsedMs };
}

// A stuck verdict is checked against the media itself before anything acts on
// it. The switch sends RTP down a pool line from the moment it is answered
// (parked or bridged), and inbound-rtp only counts packets SRTP could decrypt,
// so a rising packetsReceived proves the handshake finished whatever the
// aggregate state says. Two readings this far apart hold ~100 packets at 20 ms
// ptime; comfort-noise frames count too.
export const MEDIA_STUCK_PROBE_MS = 2_000;

export type MediaFlowVerdict =
  // The connection reached "connected" while the probe ran: the line healed
  // on its own, which says nothing about the stuck verdict being wrong.
  | 'healed_during_probe'
  // Inbound RTP is being decrypted: the line works and must not be touched.
  | 'flowing'
  | 'stalled';

// Counters are null where getStats has no inbound-rtp entry yet, which is
// what a line whose handshake never finished looks like. Only the current
// state counts: a connection that was connected and has since gone "failed"
// with ICE up lost its DTLS transport, and is exactly what a rebuild is for.
export function assessMediaFlow(input: {
  connectionState: string;
  packetsBefore: number | null;
  packetsAfter: number | null;
}): MediaFlowVerdict {
  if (input.connectionState === 'connected') return 'healed_during_probe';
  if ((input.packetsAfter ?? 0) > (input.packetsBefore ?? 0)) return 'flowing';
  return 'stalled';
}

export interface MediaRebuildEpisode {
  attempts: number;
  gaveUp: boolean;
}

export const IDLE_MEDIA_REBUILD_EPISODE: MediaRebuildEpisode = { attempts: 0, gaveUp: false };

export type MediaRebuildDecision =
  | { action: 'rebuild'; attempt: number; delayMs: number }
  // Attempts exhausted just now: tell the agent.
  | { action: 'give_up' }
  // The agent's rebuild switch (users.softphone_media_rebuild) is off: report
  // the stuck line and touch nothing.
  | { action: 'detect_only' }
  // Only a pool line has a seat-safe way back (the line-restore path); any
  // other call is left alone rather than hung up.
  | { action: 'skip'; reason: 'gave_up' | 'not_pool_line' };

export function nextMediaRebuild(
  episode: MediaRebuildEpisode,
  input: { poolLine: boolean; enabled: boolean }
): { episode: MediaRebuildEpisode; decision: MediaRebuildDecision } {
  if (!input.poolLine) return { episode, decision: { action: 'skip', reason: 'not_pool_line' } };
  if (!input.enabled) return { episode, decision: { action: 'detect_only' } };
  if (episode.gaveUp) return { episode, decision: { action: 'skip', reason: 'gave_up' } };
  if (episode.attempts >= MEDIA_REBUILD_MAX_ATTEMPTS) {
    return { episode: { ...episode, gaveUp: true }, decision: { action: 'give_up' } };
  }
  const attempt = episode.attempts + 1;
  return {
    episode: { attempts: attempt, gaveUp: false },
    decision: { action: 'rebuild', attempt, delayMs: MEDIA_REBUILD_DELAYS_MS[attempt - 1] ?? MEDIA_REBUILD_DELAYS_MS[MEDIA_REBUILD_DELAYS_MS.length - 1] },
  };
}

// The seat as a fresh membership read shows it, taken right before the
// un-REGISTER. `inConversation` mirrors the server's own test: a bridged
// contact leg or an inbound call.
export interface MediaRebuildSeat {
  state: string;
  inConversation: boolean;
}

// Whether the un-REGISTER may go ahead, from the pool's line-loss handling:
// a free or connecting seat is held and its leg reaped; a manual one keeps
// its seat through the reap; a busy one in a conversation keeps the line
// (and its seat through the hangup that follows). Everything else waits:
// an idle seat is released when its leg is reaped, wrap-up can time out into
// idle mid-rebuild, a busy seat with no conversation is released by its
// leg's hangup, and no reading (poll failed, not seated, backend restarting)
// proves any of the above.
export function mediaRebuildSeatGate(seat: MediaRebuildSeat | null): { go: true; prospectBridged: boolean } | { go: false } {
  if (!seat) return { go: false };
  if (seat.state === 'free' || seat.state === 'connecting' || seat.state === 'manual') return { go: true, prospectBridged: false };
  if (seat.state === 'busy' && seat.inConversation) return { go: true, prospectBridged: true };
  return { go: false };
}

// The line is still up after the un-REGISTER and the reap wait. Hanging it up
// from this side is seat-safe only when a prospect is bridged: the pool keeps
// a busy member in a conversation through their leg's hangup. A parked line
// the switch did not reap means the pool never saw the un-REGISTER, and a BYE
// there releases the seat as a dropped line — so it is kept, and the rebuild
// counts as failed.
export function stuckLineFallback(input: { prospectBridged: boolean }): 'hang_up' | 'keep' {
  return input.prospectBridged ? 'hang_up' : 'keep';
}

// The membership read behind the seat gate is bounded so its answer is
// seconds old at most when the un-REGISTER goes out.
export const MEDIA_REBUILD_SEAT_READ_TIMEOUT_MS = 3_000;

// A rebuild that cannot go yet (seat out of rotation or unreadable, phone
// mid-reconnect) re-checks on this cadence without spending an attempt, up to
// the cap — past it the attempt is spent as failed.
export const MEDIA_REBUILD_DEFER_RECHECK_MS = 5_000;
const MEDIA_REBUILD_DEFER_MAX_MS = 10 * 60_000;

export function mediaRebuildDeferralExpired(deferredSinceMs: number, nowMs: number): boolean {
  return nowMs - deferredSinceMs >= MEDIA_REBUILD_DEFER_MAX_MS;
}

export function mediaRebuildConfirmMs(prospectBridged: boolean): number {
  return prospectBridged ? MEDIA_REBUILD_BRIDGED_CONFIRM_MS : MEDIA_REBUILD_CONFIRM_MS;
}
